import ThemeName from "@/types/styles/ThemeName";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faArrowDownShortWide, faArrowDownWideShort, faAtom, faBan, faCalendarDays, faCancel, faCheck, faCheckCircle, faCheckSquare, faChevronDown, faChevronUp, faClipboardList, faClockRotateLeft, faCodeBranch, faCommentAltLines, faCopy, faDash, faDatabase, faDownload, faExclamationTriangle, faExternalLink, faFileChartColumn, faFileCheck, faFileSearch, faFilters, faFlag, faForwardFast, faGears, faHospital, faInfoCircle, faInputText, faLink, faListOl, faLock, faLockA, faObjectGroup, faPenAlt, faPencil, faPeopleGroup, faPlay, faQuestion, faQuestionCircle, faRefresh, faRepeat, faSave, faSearch, faSitemap, faSquare, faSquareList, faTable, faTableList, faText, faTimeline, faTimer, faTimes, faTimesHexagon, faTrashAlt, faUnlink, faUser, faUserGroup } from "@fortawesome/pro-duotone-svg-icons";
import { faPlay as falPlay } from "@fortawesome/pro-regular-svg-icons";
import { faEllipsisV, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { Customer, GetClientAppSettingsResponse, NoteStatus, NoteType, SentrySettings } from "./api/dtos";
import { ISelectItem } from "./components/forms/inputs/Select";
import { ThemeOptions } from "@mui/material";
import { faCirclePlay, faClipboardListCheck } from "@fortawesome/pro-light-svg-icons";

export interface IPaletteSettings {
    primaryLight: string;
    secondaryLight: string;
    primaryDark: string;
    secondaryDark: string;
};

export interface IClientSettings {
    eventStreamUrl?: string;
    eventStreamDebug?: boolean;
    baseUrl?: string;
    basePath?: string;
};

export interface IChannelSettings {
    updateStatus: string;
    trial: string;
    masterGroup: string;
    coordinatingGroup: string;
    institution: string;
    participant: string;

    caseReportFormState: string;
    review: string;
    verification: string;
    notApplicable: string;

    note: string;
    finding: string;
};

export interface ICtcaeSettings {
    specificationItemName: string;
    termItemName: string;
    termId: string;
    url: string;
};

export interface IMetadataSettings {
    studyEventNullKey: string;
    itemGroupUngroupedKey: string;
};

export interface IIconSettings {
    trial: IconProp;
    masterGroup: IconProp;
    coordinatingGroup: IconProp;
    institution: IconProp;
    participant: IconProp;

    caseReportForm: IconProp;
    review: IconProp;
    verification: IconProp;
    combined: IconProp;
    note: IconProp;
    audit: IconProp;
    finding: IconProp;
    report: IconProp;
    configuration: IconProp;
    sourceData: IconProp;
    utilities: IconProp;

    event: IconProp;
    form: IconProp;
    formVersion: IconProp;
    itemGroup: IconProp;
    itemGrid: IconProp;
    table: IconProp;
    item: IconProp;
    itemValue: IconProp;

    ascending: IconProp;
    descending: IconProp;
    add: IconProp;
    delete: IconProp;
    edit: IconProp;
    copy: IconProp;
    refresh: IconProp;
    close: IconProp;
    locked: IconProp;
    disabled: IconProp;
    notApplicable: IconProp;
    search: IconProp;
    filter: IconProp;
    help: IconProp;
    save: IconProp;
    cancel: IconProp;

    
    externalLink: IconProp;
    link: IconProp;
    unlink: IconProp;
    download: IconProp;

    alertSuccess: IconProp;
    alertInformation: IconProp;
    alertWarning: IconProp;
    alertError: IconProp;
    alertDefault: IconProp;

    priorityInherit: IconProp;
    priorityLow: IconProp;
    priorityNormal: IconProp;
    priorityHigh: IconProp;
    priorityCritical: IconProp;

    statusNone: IconProp;
    statusSkipped: IconProp;
    statusLocked: IconProp;
    statusStarted: IconProp;
    statusComplete: IconProp;
    statusScheduled: IconProp;

    itemValueCheckboxUnchecked: IconProp;
    itemValueCheckboxChecked: IconProp;
    itemValueDate: IconProp;
    itemValueText: IconProp;

    studyEventRepeat: IconProp;
    itemGroupRepeat: IconProp;

    user: IconProp;

    auditInsert: IconProp;
    auditUpdate: IconProp;
    auditDelete: IconProp;
    auditUnknown: IconProp;

    auditTable: IconProp;
    auditTimeline: IconProp;
};

export interface ISentrySettings {
    dsn?: string;
    customer: string;
    environment: string;
    sendDefaultPii: boolean;
    enableMetrics: boolean;
    attachStacktrace: boolean;
    debug: boolean;
    application: string;
    release?: string;
    replaysSessionSampleRate: number;
    replaysOnErrorSampleRate: number;
    enableLogs: boolean;
    tracesSampleRate: number;
    profileSessionSampleRate: number;
    enableReportABug: boolean;
}

export interface IAppSettings {
    overrideTheme?: ThemeName;
    client?: IClientSettings;
    palette: IPaletteSettings;
    icons: IIconSettings;
    channels: IChannelSettings;
    debug?: boolean;
    allowNotesAndQueriesEdit?: boolean;
    allowEventDelete?: boolean;
    metadata: IMetadataSettings;
    ctcae: ICtcaeSettings;
    homeUrl: string;
    openClinicaUrl: string;
    customer: Customer;
    noteStatusColors: Record<NoteStatus, string | undefined>;
    noteTypes: Record<NoteType, boolean>;
    noteActions: Record<NoteStatus, INoteActions>;
    noteTypeTitles: Record<NoteType, ISelectItem<number>[] | undefined>;
    findingReferences: ISelectItem<string>[] | undefined;
    sentry: ISentrySettings;
    showLayout: boolean;
};

const ClientAppSettingsResponse: GetClientAppSettingsResponse | undefined = (window as any).APP_SETTINGS;



const trogFindingReferenceOptions: ISelectItem<string>[] | undefined = [
    {
        id: 'A – Consent Procedures',
        name: 'A – Consent Procedures'
    },
    {
        id: 'B – Inclusion/Exclusion Criteria',
        name: 'B – Inclusion/Exclusion Criteria'
    },
    {
        id: 'C – Concomitant Medication/Therapy',
        name: 'C – Concomitant Medication/Therapy'
    },
    {
        id: 'D – Laboratory Assessments/Procedures',
        name: 'D – Laboratory Assessments/Procedures'
    },
    {
        id: 'E – Study Procedures',
        name: 'E – Study Procedures'
    },
    {
        id: 'F – Serious Adverse Event Reporting/Unanticipated Adverse Device Effect',
        name: 'F – Serious Adverse Event Reporting/Unanticipated Adverse Device Effect'
    },
    {
        id: 'G – Randomization Procedures/Study Drug Dosing',
        name: 'G – Randomization Procedures/Study Drug Dosing'
    },
    {
        id: 'H – Visit Schedule/Interval',
        name: 'H – Visit Schedule/Interval'
    },
    {
        id: 'I – Efficacy Ratings',
        name: 'I – Efficacy Ratings'
    },
    {
        id: 'J – Other (i.e. GCP, Sponsor procedures)',
        name: 'J – Other (i.e. GCP, Sponsor procedures)'
    },
    {
        id: 'K – Participant identifying data',
        name: 'K – Participant identifying data'
    }
]

const bctTitleOptions: Record<NoteType, ISelectItem<number>[] | undefined> = {
    [NoteType.SiteQuery]: [
        {
            id: 1,
            name: 'Missing data'
        },
        {
            id: 2,
            name: 'Review data entry'
        },
        {
            id: 3,
            name: 'Source data'
        },
        {
            id: 4,
            name: 'Medical Review query'
        },
        {
            id: 5,
            name: 'Submit form'
        },
        {
            id: 6,
            name: 'Other'
        },
    ],
    [NoteType.ReasonForChange]: [
        {
            id: 1,
            name: 'Data entry error'
        },
        {
            id: 2,
            name: 'Additional information'
        },
        {
            id: 3,
            name: 'Obvious correction'
        },
        {
            id: 4,
            name: 'Redaction'
        },
        {
            id: 5,
            name: 'Other'
        }
    ],
    [NoteType.ProtocolDeviation]: [
        {
            id: 1,
            name: 'Informed consent'
        },
        {
            id: 2,
            name: 'Eligibility'
        },
        {
            id: 3,
            name: 'Drug Management'
        },
        {
            id: 4,
            name: 'Treatment Guidelines'
        },
        {
            id: 5,
            name: 'Assessment Schedule'
        },
        {
            id: 6,
            name: 'Pathology'
        },
        {
            id: 7,
            name: 'Protocol compliance other'
        },
        {
            id: 8,
            name: 'PI Oversight/General site process'
        }
    ],
    [NoteType.ImportantProtocolDeviation]: [
        {
            id: 1,
            name: 'IPD-01 Participant informed consent not completed in full prior to study procedures'
        },
        {
            id: 2,
            name: 'IPD-02 Participant eligibility criteria not met'
        },
        {
            id: 3,
            name: 'IPD-03 SAE or AESI reporting and management not completed per protocol'
        },
        {
            id: 4,
            name: 'IPD-04 Prohibited concomitant medications per protocol'
        },
        {
            id: 5,
            name: 'IPD-05 Treatment non-compliance'
        },
        {
            id: 6,
            name: 'IPD-06 Protocol discontinuation criteria not followed'
        },
        {
            id: 7,
            name: 'IPD-07 Stratification factor errors'
        },
        {
            id: 8,
            name: 'IPD-Other'
        }
    ],
    [NoteType.Annotation]: undefined,
    [NoteType.InternalQuery]: undefined,
    [NoteType.System]: undefined,
}


const trogTitleOptions: Record<NoteType, ISelectItem<number>[] | undefined> = {
    [NoteType.SiteQuery]: undefined,
    [NoteType.ReasonForChange]: undefined,
    [NoteType.ProtocolDeviation]: [
        {
            id: 1,
            name: 'A – Consent Procedures'
        },
        {
            id: 2,
            name: 'B – Inclusion/Exclusion Criteria'
        },
        {
            id: 3,
            name: 'C – Concomitant Medication/Therapy'
        },
        {
            id: 4,
            name: 'D – Laboratory Assessments/Procedures'
        },
        {
            id: 5,
            name: 'E – Study Procedures'
        },
        {
            id: 6,
            name: 'F – Serious Adverse Event Reporting/Unanticipated Adverse Device Effect'
        },
        {
            id: 7,
            name: 'G – Randomization Procedures/Study Drug Dosing'
        },
        {
            id: 8,
            name: 'H – Visit Schedule/Interval'
        },
        {
            id: 9,
            name: 'I – Efficacy Ratings'
        },
        {
            id: 10,
            name: 'J – Other (i.e. GCP, Sponsor procedures)'
        },
        {
            id: 11,
            name: 'K – Participant identifying data'
        }
    ],
    [NoteType.ImportantProtocolDeviation]: undefined,
    [NoteType.Annotation]: undefined,
    [NoteType.InternalQuery]: undefined,
    [NoteType.System]: undefined,
}


interface INoteActions {
    reset: boolean;
    reopen: boolean;
    close: boolean;
    addAdditionalInformation: boolean;
    resolutionProposed: boolean;
}

const bctNoteActions: Record<NoteStatus, INoteActions> = {
    [NoteStatus.New]: {
        reset: false,
        reopen: false,
        close: true,
        addAdditionalInformation: true,
        resolutionProposed: true
    },
    [NoteStatus.Updated]: {
        reset: false,
        reopen: false,
        close: true,
        addAdditionalInformation: true,
        resolutionProposed: false
    },
    [NoteStatus.ResolutionProposed]: {
        reset: true,
        reopen: false,
        close: true,
        addAdditionalInformation: true,
        resolutionProposed: false
    },
    [NoteStatus.Closed]: {
        reset: false,
        reopen: true,
        close: false,
        addAdditionalInformation: true,
        resolutionProposed: false
    },
    [NoteStatus.NotApplicable]: {
        reset: false,
        reopen: false,
        close: false,
        addAdditionalInformation: true,
        resolutionProposed: false
    }
}

const trogNoteActions: Record<NoteStatus, INoteActions> = {
    [NoteStatus.New]: {
        reset: false,
        reopen: false,
        close: true,
        addAdditionalInformation: true,
        resolutionProposed: false
    },
    [NoteStatus.Updated]: {
        reset: false,
        reopen: false,
        close: true,
        addAdditionalInformation: false,
        resolutionProposed: false
    },
    [NoteStatus.ResolutionProposed]: {
        reset: true,
        reopen: false,
        close: true,
        addAdditionalInformation: false,
        resolutionProposed: false
    },
    [NoteStatus.Closed]: {
        reset: false,
        reopen: true,
        close: false,
        addAdditionalInformation: false,
        resolutionProposed: false
    },
    [NoteStatus.NotApplicable]: {
        reset: false,
        reopen: false,
        close: false,
        addAdditionalInformation: false,
        resolutionProposed: false
    }
}

const bctNoteStatusColors: Record<NoteStatus, string | undefined> = {
    [NoteStatus.New]: undefined,
    [NoteStatus.Updated]: undefined,
    [NoteStatus.ResolutionProposed]: undefined,
    [NoteStatus.Closed]: undefined,
    [NoteStatus.NotApplicable]: undefined,
}

type StatusColorOptions = "primary" | "secondary" | "info" | "error" | "warning" | "success";


const trogNoteStatusColors: Record<NoteStatus, StatusColorOptions | undefined> = {
    [NoteStatus.New]: "error",
    [NoteStatus.Updated]: undefined,
    [NoteStatus.ResolutionProposed]: "info",
    [NoteStatus.Closed]: undefined,
    [NoteStatus.NotApplicable]: undefined,
}

const GetCustomer = (customerId: string): Customer => {
    switch (customerId?.toLowerCase()) {
        case "1":
        case "bct":
            return Customer.Bct;
        case "2":
        case "trog":
            return Customer.Trog;
        default:
            return Customer.Unknown;
    }
}

const GetPaletteColor = (color?: string): string | undefined => {
    if (!color || color.trim() === "") {
        return undefined;
    }

    if (color.startsWith('#')) {
        return color;
    }

    return `#${color}`;
}

const GetNoteTypeArray = (noteTypes?: string): NoteType[] => {
    if (!noteTypes) {
        return [];
    }

    const types: NoteType[] = []

    for (let type of noteTypes.split(','))
    {
        try {
            types.push(parseInt(type) as NoteType)
        }
        catch {

        }
    }

    return types;
}

const GetNoteTypes = (noteTypes?: NoteType[]): Record<NoteType, boolean> | undefined => {
    if (!noteTypes) {
        return undefined;
    }

    const result: Record<NoteType, boolean> = {
        [NoteType.Annotation]: false,
        [NoteType.ImportantProtocolDeviation]: false,
        [NoteType.InternalQuery]: false,
        [NoteType.ProtocolDeviation]: false,
        [NoteType.ReasonForChange]: false,
        [NoteType.SiteQuery]: false,
        [NoteType.System]: false
    }

    for (let noteType of noteTypes) {
        result[noteType] = true;
    }

    return result;
}

const customer = ClientAppSettingsResponse?.settings?.customer ?? GetCustomer(import.meta.env.VITE_CUSTOMER);

const getSentryAppSettingValue = <TData>(
    appSettingProperty: keyof SentrySettings | undefined
): TData | undefined => {
    if (!appSettingProperty) {
        return undefined;
    }
    
    const value: TData | undefined = ClientAppSettingsResponse?.settings?.sentry?.[appSettingProperty] as TData;
    
    if (value === undefined || value === null) {
        return undefined;
    }

    return value;
}

const getSentryEnvironmentValue = (
    environmentProperty: string | undefined
): string | undefined => {
    if (!environmentProperty) {
        return undefined;
    }
    
    const value: string | undefined = import.meta.env[environmentProperty];
    
    if (value === undefined || value === null || value === "") {
        return undefined;
    }

    return value;
}

const getSentryEnvironmentString = (
    environmentProperty: string | undefined
): string | undefined => {
    return getSentryEnvironmentValue(environmentProperty);
};

const getSentryEnvironmentNumber = (
    environmentProperty: string | undefined
): number | undefined => {
    const valueStr = getSentryEnvironmentValue(environmentProperty);

    if (!valueStr) {
        return undefined;
    }

    try {
        return parseFloat(valueStr);
    }
    catch {
        return undefined;
    }
};

const getSentryEnvironmentBoolean = (
    environmentProperty: string | undefined
): boolean | undefined => {
    const valueStr = getSentryEnvironmentValue(environmentProperty);

    if (!valueStr) {
        return undefined;
    }

    const lowerCase = valueStr.toLowerCase();

    if ( lowerCase === "1" || lowerCase === 'true') {
        return true;
    }

    if (lowerCase === "0" || lowerCase === 'false') {
        return false;
    }

    return undefined;
};

const getSentryString = (appSettingProperty: keyof SentrySettings | undefined, environmentProperty: string | undefined) => {
    const appSettingValue = getSentryAppSettingValue<string>(appSettingProperty);

    if (appSettingValue) {
        return appSettingValue;
    }

    const environmentValue = getSentryEnvironmentString(environmentProperty);

    if (environmentValue) {
        return environmentValue;
    }

    return undefined;
}

const getSentryNumber = (appSettingProperty: keyof SentrySettings | undefined, environmentProperty: string | undefined) => {
    const appSettingValue = getSentryAppSettingValue<number>(appSettingProperty);

    if (appSettingValue !== null && appSettingValue !== undefined) {
        return appSettingValue;
    }

    const environmentValue = getSentryEnvironmentNumber(environmentProperty);

    if (environmentValue !== null && environmentValue !== undefined) {
        return environmentValue;
    }

    return undefined;
}

const getSentryBoolean = (appSettingProperty: keyof SentrySettings | undefined, environmentProperty: string | undefined) => {
    const appSettingValue = getSentryAppSettingValue<boolean>(appSettingProperty);

    if (appSettingValue !== null && appSettingValue !== undefined) {
        return appSettingValue;
    }

    const environmentValue = getSentryEnvironmentBoolean(environmentProperty);

    if (environmentValue !== null && environmentValue !== undefined) {
        return environmentValue;
    }

    return undefined;
}

export const AppSettings: IAppSettings = {
    //overrideTheme: 'light',
    client: {
        eventStreamUrl: import.meta.env.VITE_EVENT_STREAM_URL,
        baseUrl: import.meta.env.VITE_API_URL,
        basePath: '/api',
        eventStreamDebug: import.meta.env.DEV
    },
    palette: {
        primaryDark: ClientAppSettingsResponse?.settings?.palette?.primaryDark ?? GetPaletteColor(import.meta.env.VITE_PALETTE_PRIMARY_DARK) ?? '#434343',
        primaryLight: ClientAppSettingsResponse?.settings?.palette?.primaryLight ?? GetPaletteColor(import.meta.env.VITE_PALETTE_PRIMARY_LIGHT) ??'#000000',
        secondaryDark: ClientAppSettingsResponse?.settings?.palette?.secondaryDark ?? GetPaletteColor(import.meta.env.VITE_PALETTE_SECONDARY_DARK) ??'#151515',
        secondaryLight: ClientAppSettingsResponse?.settings?.palette?.secondaryLight ?? GetPaletteColor(import.meta.env.VITE_PALETTE_SECONDARY_LIGHT) ??'#737373'
    },
    icons: {
        trial: faAtom,
        masterGroup: faPeopleGroup,
        coordinatingGroup: faUserGroup,
        institution: faHospital,
        participant: faUser,


        caseReportForm: faClipboardList,
        verification: faFileCheck,
        review: faFileSearch,
        combined: faEllipsisV,
        note: faFlag,
        audit: faClockRotateLeft,
        finding: faCommentAltLines,
        report: faFileChartColumn,
        configuration: faGears,
        sourceData: faDatabase,
        utilities: faGears,

        event: faCalendarDays,
        form: faSquareList,
        formVersion: faCodeBranch,
        itemGroup: faObjectGroup,
        itemGrid: faTableList,
        table: faTable,
        item: faInputText,
        itemValue: faListOl,


        ascending: faArrowDownShortWide,
        descending: faArrowDownWideShort,
        add: faPlus,
        delete: faTrashAlt,
        edit: faPencil,
        copy: faCopy,
        refresh: faRefresh,
        close: faTimes,
        locked: faLock,
        disabled: faBan,
        notApplicable: faDash,
        search: faSearch,
        filter: faFilters,
        help: faQuestionCircle,
        save: faSave,
        cancel: faCancel,

        externalLink: faExternalLink,
        link: faLink,
        unlink: faUnlink,
        download: faDownload,

        alertSuccess: faCheckCircle,
        alertInformation: faInfoCircle,
        alertWarning: faExclamationTriangle,
        alertError: faTimesHexagon,
        alertDefault: faBan,

        priorityInherit: faSitemap,
        priorityLow: faChevronDown,
        priorityNormal: faDash,
        priorityHigh: faChevronUp,
        priorityCritical: faExclamationTriangle,

        statusNone: faTimes,
        statusSkipped: faForwardFast,
        statusLocked: faLockA,
        statusStarted: falPlay,
        statusComplete: faCheck,
        statusScheduled: faTimer,

        
        itemValueCheckboxUnchecked: faSquare,
        itemValueCheckboxChecked: faCheckSquare,
        itemValueDate: faCalendarDays,
        itemValueText: faText,

        studyEventRepeat: faRepeat,
        itemGroupRepeat: faRepeat,

        user: faUser,

        auditInsert: faPlus,
        auditUpdate: faPencil,
        auditDelete: faTrashAlt,
        auditUnknown: faQuestion,

        auditTable: faTable,
        auditTimeline: faTimeline 
    },
    channels: {
        updateStatus: 'status',
        trial: 'trial',
        masterGroup: 'master-group',
        coordinatingGroup: 'coordinating-group',
        institution: 'institution',
        participant: 'participant',

        caseReportFormState: 'case-report-form-state',
        review: 'review',
        verification: 'verification',
        notApplicable: 'not-applicable',

        note: 'note',
        finding: 'finding'
    },
    debug: import.meta.env.DEV,
    allowNotesAndQueriesEdit: true,
    allowEventDelete: ClientAppSettingsResponse?.settings?.allowEventDelete ?? import.meta.env.VITE_API_ALLOW_EVENT_DELETE === "1",
    metadata: {
        itemGroupUngroupedKey: 'UNGROUPED',
        studyEventNullKey: 'NULL_KEY'
    },
    ctcae: {
        specificationItemName: 'CtcaeSpecificationItemName',
        termItemName: 'CtcaeTermItemName',
        termId: 'CtcaeTermId',
        url: ClientAppSettingsResponse?.settings?.ctcaeUrl ?? import.meta.env.VITE_API_CTCAE_URL,
    },
    homeUrl: ClientAppSettingsResponse?.settings?.homeUrl ?? import.meta.env.VITE_HOME_URL,
    openClinicaUrl: ClientAppSettingsResponse?.settings?.openClinicaUrl ?? import.meta.env.VITE_OPENCLINICA_URL,
    customer: customer,
    noteTypes: GetNoteTypes(ClientAppSettingsResponse?.settings?.noteTypes) ?? GetNoteTypes(GetNoteTypeArray(import.meta.env.VITE_NOTE_TYPES)) ?? {
        [NoteType.Annotation]: false,
        [NoteType.ImportantProtocolDeviation]: false,
        [NoteType.InternalQuery]: false,
        [NoteType.ProtocolDeviation]: false,
        [NoteType.ReasonForChange]: false,
        [NoteType.SiteQuery]: false,
        [NoteType.System]: false
    },
    noteTypeTitles: customer === Customer.Bct ? bctTitleOptions : trogTitleOptions,
    noteStatusColors: customer === Customer.Bct ? bctNoteStatusColors : trogNoteStatusColors,
    noteActions: customer === Customer.Bct ? bctNoteActions : trogNoteActions,
    sentry: {
        dsn: getSentryString('dsn', 'VITE_SENTRY_DSN'),
        debug: getSentryBoolean('debug', 'VITE_SENTRY_DEBUG') ?? false,
        enableMetrics: getSentryBoolean('enableMetrics', 'VITE_SENTRY_ENABLE_METRICS') ?? false,
        sendDefaultPii: getSentryBoolean('sendDefaultPii', 'VITE_SENTRY_SEND_DEFAULT_PII') ?? false,
        attachStacktrace: getSentryBoolean('attachStacktrace', 'VITE_SENTRY_ATTACH_STACKTRACE') ?? false,
        customer: getSentryString('customer', 'VITE_SENTRY_CUSTOMER') ?? 'Unknown',
        environment:  getSentryString('environment', 'VITE_SENTRY_ENVIRONMENT') ?? 'Development',
        application: getSentryString(undefined, 'VITE_SENTRY_APPLICATION') ?? 'SourceDataVerification',
        release: getSentryString(undefined, 'VITE_SENTRY_RELEASE'),
        enableLogs: getSentryBoolean('enableLogs', 'VITE_SENTRY_ENABLE_LOGS') ?? false,
        profileSessionSampleRate: getSentryNumber('profileSessionSampleRate', 'VITE_SENTRY_PROFILE_SESSION_SAMPLE_RATE') ?? 1.0,
        replaysOnErrorSampleRate: getSentryNumber('replaysOnErrorSampleRate', 'VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE') ?? 1.0,
        replaysSessionSampleRate: getSentryNumber('replaysSessionSampleRate', 'VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE') ?? 0.1,
        tracesSampleRate: getSentryNumber('tracesSampleRate', 'VITE_SENTRY_TRACE_SAMPLE_RATE') ?? 0.2,

        enableReportABug: getSentryBoolean('enableReportABug', 'VITE_SENTRY_ENABLE_REPORT_A_BUG') ?? false
    },
    showLayout: new URLSearchParams(location.search).get("hide-layout") !== '1',
    findingReferences: undefined//customer === Customer.Trog ? trogFindingReferenceOptions : undefined
};